import { memo, useState, useCallback, useEffect } from "react"
import { useAuthState } from "@/hooks/useAuth"
import { useAnalytics } from "@/hooks/useAnalytics"
import type { PortfolioItem } from "@/types/models"
import { AuthModal } from "@/components/auth/AuthModal"
import { saveItemToCollections, getItemSaveState } from "@/lib/savesApi"
import { CollectionSelectSheet } from "./CollectionSelectSheet"

interface SaveButtonProps {
  item: PortfolioItem
  className?: string
  onSaveStateChange?: (isSaved: boolean, newSavesCount: number) => void
  iconSize?: string
  source?: string
}

export const SaveButton = memo(function SaveButton({ 
  item, 
  className = "", 
  onSaveStateChange, 
  iconSize = "w-4 h-4",
  source
}: SaveButtonProps) {
  const { user } = useAuthState()
  const { trackEvent } = useAnalytics()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [showCollectionSheet, setShowCollectionSheet] = useState(false)
  
  // Local state for optimistic updates
  const [localSaveState, setLocalSaveState] = useState<{isSaved: boolean, savesCount: number}>({
    isSaved: false,
    savesCount: 0
  })

  // Fetch initial save state
  useEffect(() => {
    let mounted = true
    
    const fetchInitialState = async () => {
      try {
        const state = await getItemSaveState(item.id, user?.uid || null, Boolean(item.isFlash))
        if (mounted) {
          setLocalSaveState(state)
          onSaveStateChange?.(state.isSaved, state.savesCount)
        }
      } catch (error) {
        console.error("Error fetching initial save state:", error)
      }
    }

    fetchInitialState()
    return () => { mounted = false }
  }, [item.id, item.isFlash, user?.uid])

  const handleSaveClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (!user) {
      setShowAuthModal(true)
      return
    }
    
    // Open the collection selection sheet
    setShowCollectionSheet(true)
  }, [user])

  const handleSaveToCollections = useCallback(async (selectedCollectionIds: string[]) => {
    if (!user) return

    try {
      await saveItemToCollections(
        item.id, 
        user.uid, 
        Boolean(item.isFlash), 
        selectedCollectionIds
      )
      
      // Track the save action
      const isNowSaved = selectedCollectionIds.length > 0
      trackEvent('item_saved', {
        item_id: item.id,
        item_type: item.isFlash ? 'flash' : 'tattoo',
        artist_id: item.artistId,
        source,
        is_saved: isNowSaved
      })
      
      // Refetch save state to update UI
      const newState = await getItemSaveState(item.id, user.uid, Boolean(item.isFlash))
      setLocalSaveState(newState)
      onSaveStateChange?.(newState.isSaved, newState.savesCount)
    } catch (error) {
      console.error("Error saving to collections:", error)
    }
  }, [user, item.id, item.isFlash, item.artistId, trackEvent, onSaveStateChange, source])

  const handleAuthSuccess = () => {
    setShowCollectionSheet(true)
  }

  return (
    <>
      <button 
        onClick={handleSaveClick}
        className={`text-foreground text-xs font-medium px-2 py-1 flex items-center gap-1 transition-colors ${className}`}
      >
        {localSaveState.isSaved ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={`${iconSize} text-[color:var(--save-active)]`}>
            <path d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={iconSize}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0 1 11.186 0Z" />
          </svg>
        )}
        {localSaveState.savesCount > 0 && (
          <span>{localSaveState.savesCount}</span>
        )}
      </button>

      <AuthModal
        isOpen={showAuthModal}
        onOpenChange={setShowAuthModal}
        onSuccess={handleAuthSuccess}
        source="save_button"
        defaultMode="signup"
      />

      <CollectionSelectSheet
        isOpen={showCollectionSheet}
        onOpenChange={setShowCollectionSheet}
        item={item}
        onSave={handleSaveToCollections}
      />
    </>
  )
}) 
